import React, { PointerEvent, useEffect, useRef, useState } from "react";
import { Box, Button } from "@mui/material";

// Set to true to reactivate the code overlay and its reveal flow.
export const PROMOTION_SCRATCH_ENABLED = false;

const WIDTH = 600;
const HEIGHT = 260;
const RADIUS = 30;
const COLUMNS = 24;
const ROWS = 12;

type Point = { x: number; y: number };

export default function PromotionScratchCard({ percentage, code, revealed, onReveal }: {
  percentage: number; code: string; revealed: boolean; onReveal: () => void;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const context = useRef<CanvasRenderingContext2D | null>(null);
  const previous = useRef<(Point & { pointerId: number }) | null>(null);
  const cleared = useRef(new Set<number>());
  const completed = useRef(revealed);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    completed.current = revealed;
    if (revealed) return;
    let ctx: CanvasRenderingContext2D | null = null;
    try { ctx = canvas.current?.getContext("2d") || null; } catch { /* Use the reveal button instead. */ }
    context.current = ctx;
    if (!ctx) return;
    const foil = ctx.createLinearGradient(0, 0, WIDTH, HEIGHT);
    foil.addColorStop(0, "#e2d7c5");
    foil.addColorStop(0.4, "#aebbc2");
    foil.addColorStop(0.7, "#e8dfd1");
    foil.addColorStop(1, "#b4b9b8");
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = foil;
    ctx.fillRect(0, 0, WIDTH, HEIGHT);
    ctx.strokeStyle = "rgba(255,255,255,0.2)";
    ctx.lineWidth = 1;
    for (let x = -HEIGHT; x < WIDTH; x += 12) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x + HEIGHT, HEIGHT); ctx.stroke();
    }
    ctx.fillStyle = "#303638";
    ctx.textAlign = "center";
    ctx.font = "600 38px sans-serif";
    ctx.fillText("SCRATCH TO REVEAL", WIDTH / 2, 95);
    cleared.current.clear();
    setReady(true);
  }, [revealed]);

  const reveal = () => {
    if (completed.current) return;
    completed.current = true;
    previous.current = null;
    onReveal();
  };

  const scratch = (from: Point, to: Point) => {
    const ctx = context.current;
    if (!ctx || completed.current) return;
    ctx.globalCompositeOperation = "destination-out";
    ctx.lineWidth = RADIUS * 2;
    ctx.lineCap = "round";
    ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.lineTo(to.x, to.y); ctx.stroke();
    ctx.beginPath(); ctx.arc(to.x, to.y, RADIUS, 0, Math.PI * 2); ctx.fill();
    // Count distinct cleared areas, so rubbing the same spot cannot finish the ticket.
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const lengthSquared = dx * dx + dy * dy;
    for (let row = 0; row < ROWS; row++) {
      for (let column = 0; column < COLUMNS; column++) {
        const x = (column + 0.5) * WIDTH / COLUMNS;
        const y = (row + 0.5) * HEIGHT / ROWS;
        const along = lengthSquared ? Math.max(0, Math.min(1, ((x - from.x) * dx + (y - from.y) * dy) / lengthSquared)) : 0;
        if ((x - from.x - along * dx) ** 2 + (y - from.y - along * dy) ** 2 <= RADIUS ** 2) {
          cleared.current.add(row * COLUMNS + column);
        }
      }
    }
    if (cleared.current.size / (COLUMNS * ROWS) >= 1 / 3) reveal();
  };

  const point = (event: PointerEvent<HTMLCanvasElement>): Point | null => {
    const bounds = event.currentTarget.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return null;
    return { x: Math.max(0, Math.min(WIDTH, (event.clientX - bounds.left) * WIDTH / bounds.width)),
      y: Math.max(0, Math.min(HEIGHT, (event.clientY - bounds.top) * HEIGHT / bounds.height)) };
  };

  const start = (event: PointerEvent<HTMLCanvasElement>) => {
    if (previous.current || completed.current || (event.pointerType === "mouse" && event.button !== 0)) return;
    const location = point(event);
    if (!location) return;
    previous.current = { ...location, pointerId: event.pointerId };
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* Capture is optional. */ }
    scratch(location, location);
  };

  const move = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!previous.current || previous.current.pointerId !== event.pointerId) return;
    const location = point(event);
    if (!location) return;
    const from = previous.current;
    previous.current = { ...location, pointerId: event.pointerId };
    scratch(from, location);
  };

  const stop = (event: PointerEvent<HTMLCanvasElement>) => {
    if (previous.current?.pointerId === event.pointerId) previous.current = null;
  };

  if (revealed) return <Box role="status" sx={{ position: "absolute", width: 1, height: 1,
    p: 0, m: -1, overflow: "hidden", clip: "rect(0, 0, 0, 0)", whiteSpace: "nowrap" }}>
    Your offer: {percentage}% off. Code {code}.
  </Box>;

  return <Box role="group" aria-label="Promotion ticket" sx={{ position: "absolute", inset: 0, borderRadius: "inherit", overflow: "hidden" }}>
    {!ready && <Box aria-hidden="true" sx={{ position: "absolute", inset: 0, pt: 1, textAlign: "center", bgcolor: "#c8cdcc", color: "#303638", fontSize: 10 }}>Scratch to reveal</Box>}
    <canvas ref={canvas} width={WIDTH} height={HEIGHT} aria-hidden="true"
      onPointerDown={start} onPointerMove={move} onPointerUp={stop} onPointerCancel={stop} onLostPointerCapture={stop}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", touchAction: "none", cursor: "crosshair" }} />
    <Button onClick={reveal} sx={{ position: "absolute", bottom: 5, left: "50%", transform: "translateX(-50%)",
      color: "#303638", fontSize: 10, lineHeight: 1.2, minWidth: 0, px: 0.5, py: 0.25, whiteSpace: "nowrap", textTransform: "none",
      "&:hover": { background: "rgba(255,255,255,0.25)" },
    }}>Reveal code</Button>
  </Box>;
}
